import { useEffect, useRef, useState, createContext, useContext, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import logger from "./lib/logger";
import { setupRevenueCat, logoutRevenueCat } from "./lib/revenuecat";
import { loadContentOverrides } from "./lib/contentOverrides";
import { installErrorReporting } from "./lib/errorReporter";
import { installGoogleAnalytics } from "./lib/analytics";
import { captureReferrerFromUrl, attachStoredReferrer, readStoredReferrer } from "./lib/referrals";
import { migrateGuestSessionIfPresent } from "./lib/guestSession";
import { redeemPendingPromoCode } from "./lib/promoRedeem";
import { fetchReminderPrefs, applySchedule, getCapabilities as getReminderCaps } from "./lib/practiceReminders";

// Install global error reporting once, as early as possible, so we capture
// boot-time failures (axios config issues, missing env vars, etc.) in
// addition to runtime crashes.
installErrorReporting();

// Google Analytics 4 (gtag.js) — web only. Skipped inside Capacitor iOS
// so we don't ship the tracking domain into the App Store binary.
installGoogleAnalytics();

// Capture ?ref=<user_id> from the URL on very first load and stash it in
// localStorage so we can bind the referral once the visitor completes
// sign-up (possibly minutes or hours later).
captureReferrerFromUrl();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Cross-platform auth transport ──────────────────────────────────────────
// Everyone (web, iOS, Android) now uses Bearer-token auth via the
// Authorization header. We used to fall back to HttpOnly cookies on web,
// but the production proxy strips our `CORS_ORIGINS` list and injects
// `Access-Control-Allow-Origin: *` on every response. Combined with
// `Access-Control-Allow-Credentials: true` from the backend, that violates
// the W3C CORS spec and WebKit (mobile Safari especially) refuses to
// expose the response — axios surfaces this as a generic "Network Error".
//
// Bearer tokens sidestep the whole problem: the request carries no
// credentials, so the ACAO=* wildcard is fine. Backend deps.py already
// accepts Bearer OR cookie so nothing else has to change.
axios.defaults.withCredentials = false;
// Fail-fast timeout so a hung request (typical on flaky iOS cellular /
// WebView networks) surfaces to the user instead of leaving the UI
// stuck in a spinner forever. 20 s is generous enough for TTS/Stripe
// but tight enough that users don't stare at a frozen screen. Callers
// that need a different budget can override per-request.
axios.defaults.timeout = 20000;
axios.interceptors.request.use((config) => {
  // Never send cookies — see note above.
  config.withCredentials = false;
  const tok = localStorage.getItem("session_token");
  if (tok) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${tok}`;
    }
  }
  return config;
});

// Helper called by every auth flow to persist the token returned in the
// response body. Web also stores it (so MembershipCard's localStorage read
// still works there) but only iOS strictly needs it.
export const persistSessionToken = (data) => {
  if (data && typeof data === "object" && data.session_token) {
    try { localStorage.setItem("session_token", data.session_token); } catch (_) { /* ignore quota */ }
  }
};

export const clearSessionToken = () => {
  try { localStorage.removeItem("session_token"); } catch (_) { /* ignore */ }
};

// ============ AUTH CONTEXT ============
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // 401 here usually means the session_token cookie/header is stale.
      // Explicitly clear both transports so the next sign-in starts clean.
      if (error?.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/logout`, {});
        } catch (_) { /* best-effort */ }
        clearSessionToken();
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      // eslint-disable-next-line
      setLoading(false);
      return;
    }
    checkAuth();
    // Fire-and-forget: pull admin-authored content overrides so the
    // intervention graph builder uses the latest copy. Fails silently.
    loadContentOverrides();
  }, [checkAuth]);

  // Mirror our authenticated user into RevenueCat (iOS native shell only;
  // no-op on the web). Re-runs whenever the user identity changes.
  useEffect(() => {
    if (user?.user_id) {
      setupRevenueCat(user.user_id).catch(() => { /* non-fatal */ });
      // If a ?ref= was captured before signup, bind it to this user now.
      // Idempotent server-side, so calling on every session start is safe.
      attachStoredReferrer(user.user_id).catch(() => { /* non-fatal */ });
      // Migrate any guest-mode progress from localStorage into the
      // newly-signed-in user's account. Idempotent + non-fatal so it
      // can't block sign-in.
      migrateGuestSessionIfPresent().catch(() => { /* non-fatal */ });
      // Redeem a pending promo code (e.g. CALM100) if the user typed one
      // into the signup form before hitting Apple/Google. Idempotent
      // server-side. On success the /api/billing/status refresh triggered
      // by the AuthProvider flips them to VIP.
      (async () => {
        const result = await redeemPendingPromoCode();
        if (result?.ok) {
          try {
            const { toast } = await import("sonner");
            toast.success(`Early Access activated — welcome to VIP.`);
          } catch { /* toast optional */ }
          // Force a billing refresh so the paywall guard sees VIP.
          try { window.dispatchEvent(new Event("billing:refresh")); } catch {}
        }
      })().catch(() => { /* non-fatal */ });
      // Re-apply the user's saved practice reminders on session start so
      // the OS-level schedule stays in sync (Capacitor cancels alerts
      // when the app is uninstalled+reinstalled, and web timers reset
      // per tab). Only touches the OS when permission was previously
      // granted — no permission-request side effects here.
      (async () => {
        try {
          const caps = getReminderCaps();
          if (caps.kind === "unsupported") return;
          const prefs = await fetchReminderPrefs();
          if (prefs?.enabled) {
            await applySchedule(prefs);
          }
        } catch (_) { /* non-fatal */ }
      })();
    }
  }, [user?.user_id]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    // `prompt=select_account` forces Google's account chooser instead of
    // silently re-using the cached browser session. This is what allows a
    // user whose app account was deleted (or who simply wants to switch
    // Google identities) to pick a fresh one — Emergent Auth passes this
    // param straight through to Google's OAuth request.
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&prompt=select_account`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      logger.error("Logout error:", error);
    }
    clearSessionToken();
    await logoutRevenueCat();
    setUser(null);
    window.location.href = '/';
  };

  // Wrap setUser so any consumer (EmailAuthForm, AppleSignInButton,
  // AuthCallback) that calls setUser(authResponseData) also persists the
  // session_token in localStorage for the Bearer-header interceptor.
  const setUserAndPersist = useCallback((data) => {
    persistSessionToken(data);
    setUser(data);
  }, []);

  const authValue = useMemo(
    () => ({ user, setUser: setUserAndPersist, loading, login, logout, checkAuth }),
    [user, loading, login, logout, checkAuth, setUserAndPersist]
  );

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// ============ AUTH CALLBACK ============
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        navigate('/');
        return;
      }

      const sessionId = sessionIdMatch[1];

      // CRITICAL: strip the #session_id fragment from the URL *before* the
      // async exchange. The session_id is single-use — if it stays in the
      // address bar, a stray re-render, browser back/refresh, or the user
      // copying the URL can cause a second exchange attempt which 401s
      // (token already consumed) and produces the misleading
      // "Authentication failed" we used to see after admin-side deletions.
      try {
        const cleanPath = window.location.pathname + window.location.search;
        window.history.replaceState(null, '', cleanPath || '/');
      } catch (_) { /* SSR or sandboxed env — non-fatal */ }

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          {
            session_id: sessionId,
            ...(readStoredReferrer() ? { ref: readStoredReferrer() } : {}),
          },
          { withCredentials: true }
        );
        setUser(response.data);
        // Honour any post-auth destination stashed by SignupPage (e.g.
        // "/pricing?plan=...&autoPay=1" for the pay-now flow). Falls back
        // to /dashboard for the normal trial sign-up.
        let dest = "/dashboard";
        try {
          const stashed = sessionStorage.getItem("post_auth_redirect");
          if (stashed) {
            dest = stashed;
            sessionStorage.removeItem("post_auth_redirect");
          }
        } catch (_) { /* sessionStorage unavailable — keep default */ }
        navigate(dest, { replace: true, state: { user: response.data } });
      } catch (error) {
        logger.error("Auth callback error:", error);
        // Categorise the failure so /sign-in can show accurate recovery
        // guidance instead of the confusing generic "Google stale session"
        // banner. Three buckets:
        //   • network — axios couldn't reach the server (cellular drop,
        //     Safari backgrounding, transient DNS). Not the user's fault.
        //   • stale_session — Emergent Auth's browser cookie returned an
        //     already-redeemed session_id, so /auth/session 401'd. The
        //     browser has a valid-looking Google cookie but our backend
        //     doesn't recognise the token.
        //   • unknown — anything else (rare).
        let bucket = "unknown";
        const isAxiosNetwork =
          error?.code === "ERR_NETWORK" ||
          error?.message === "Network Error" ||
          !error?.response;
        if (isAxiosNetwork) {
          bucket = "network";
        } else if (error?.response?.status === 401) {
          bucket = "stale_session";
        }
        navigate(`/sign-in?auth_error=${bucket}`, { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3090A8] mx-auto mb-4"></div>
        <p className="text-[#718096] font-nunito">Signing you in...</p>
      </div>
    </div>
  );
};

// ============ PROTECTED ROUTE ============
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3090A8]"></div>
      </div>
    );
  }

  const effectiveUser = user || location.state?.user;

  // Allow guest mode for the dial_down_body_tension practice — cold
  // visitors can try the first 4 tasks without an account. See
  // /app/frontend/src/lib/guestSession.js for the localStorage schema
  // and /app/frontend/src/pages/InterventionFlow.jsx for the Task-4
  // hard-block + upgrade modal.
  const isGuestPath =
    location.pathname === "/intervention/dial_down_tension" &&
    new URLSearchParams(location.search).get("guest") === "1";
  if (!effectiveUser && !isGuestPath) {
    return <Navigate to="/login" replace />;
  }
  if (!effectiveUser && isGuestPath) {
    return children;
  }

  return children;
};

// Mounts a scroll-to-top side-effect every time the route's `pathname`
// changes. Without this, navigating from /practices to /practices/:slug
// would land the user mid-page (where they were scrolled on the catalog).
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// ============ APP ROUTER ============
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <GlobalPaywallGuard />
      <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/login" element={<LandingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
      <Route path="/library/:practiceId" element={<ProtectedRoute><LibraryPlayerPage /></ProtectedRoute>} />
      <Route path="/disclaimer" element={<ProtectedRoute><DisclaimerPage /></ProtectedRoute>} />
      <Route path="/getting-started" element={<ProtectedRoute><GettingStartedPage /></ProtectedRoute>} />
      <Route path="/intervention/:type" element={<ProtectedRoute><InterventionFlow /></ProtectedRoute>} />
      <Route path="/intervention/:type/not-improving" element={<ProtectedRoute><NotImprovingPage /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfUsePage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/practices" element={<PracticesPage />} />
      <Route path="/practices/:slug" element={<PracticeDetailPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
    </Routes>
    </>
  );
}

// ============ COMPONENTS ============
// Lazy load components
import LandingPage from "./pages/LandingPage";
import HomePage from "./pages/HomePage";
import PricingPage from "./pages/PricingPage";
import SignupPage from "./pages/SignupPage";
import SignInPage from "./pages/SignInPage";
import LibraryPage from "./pages/LibraryPage";
import LibraryPlayerPage from "./pages/LibraryPlayerPage";
import Dashboard from "./pages/Dashboard";
import InterventionFlow from "./pages/InterventionFlow";
import HistoryPage from "./pages/HistoryPage";
import FavoritesPage from "./pages/FavoritesPage";
import SettingsPage from "./pages/SettingsPage";
import NotImprovingPage from "./pages/NotImprovingPage";
import AdminDashboard from "./pages/AdminDashboard";
import GettingStartedPage from "./pages/GettingStartedPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import DisclaimerPage from "./pages/DisclaimerPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import FaqPage from "./pages/FaqPage";
import AboutPage from "./pages/AboutPage";
import SecurityPage from "./pages/SecurityPage";
import PracticesPage from "./pages/PracticesPage";
import PracticeDetailPage from "./pages/PracticeDetailPage";
import AccountPage from "./pages/AccountPage";
import { LibraryAudioProvider } from "./contexts/LibraryAudioContext";
import { BillingProvider } from "./contexts/BillingContext";
import { LibraryMiniPlayer } from "./components/LibraryMiniPlayer";
import { GlobalPaywallGuard } from "./components/GlobalPaywallGuard";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BillingProvider>
          <LibraryAudioProvider>
            <div className="App font-nunito">
              <Toaster position="top-center" richColors />
              <AppRouter />
              <LibraryMiniPlayer />
            </div>
          </LibraryAudioProvider>
        </BillingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
